// Tournament CREATE / EDIT modal logic.
//
// Extracted verbatim from js/app.js. Covers:
//  - The create-tournament modal: form submit (create + edit share one form),
//    the multi-group settings tabs editor inside it, template choose/save UI,
//    and the "New Tournament" choice modal (from scratch vs from template).
//  - The edit-tournament modal (same DOM as create, re-labelled + pre-filled).
//  - Round-count suggestion (Round Robin) and rating recalculation on type change.
//
// Deliberately left in app.js (too tangled with the notifications/cooperation
// region to move safely):
//  - renderCooperatingArbitersList() and the whole cooperation/notifications
//    block (invite/retract/remove arbiter, notification dropdown). The create
//    and edit flows call it, so it's passed in via `deps.renderCooperatingArbitersList`.
//  - The tie-break editor (#btn-edit-tiebreaks / #edit-tiebreaks-modal) — reads
//    getActiveSettings().tiebreaks but is otherwise self-contained and sits right
//    next to the cooperation block; left alone per the "don't touch that region" rule.
//  - renderDashboardGroupsBar() (dashboard groups tab bar, not the modal editor) and
//    updateUIState()/renderTournamentsPage() (broad app-wide renderers used by many
//    other flows) stay in app.js and are passed in as dependencies.
//
// The logic that does NOT need the DOM — defaults, normalisation, the mapping
// between this form's values and the stored tournament/group/template shapes,
// validation, and the small label/summary builders — lives in
// js/utils/tournament-form-logic.js and is tested in
// tests/tournament-form-logic.test.js. What is left here reads inputs, writes
// inputs, and shows or hides things.
//
// initTournamentForm(deps) wires everything up; call once from app.js.
//  deps.getPlayerDisplayRating: from ui/players.js — used to recompute ratings
//    when the rating type (standard/rapid/blitz) changes on an edited tournament.
//  deps.renderCooperatingArbitersList: defined in app.js (cooperation/notifications
//    region); refreshes the invite list shown inside this modal.
//  deps.updateUIState, deps.renderPlayerList, deps.renderCurrentRound,
//    deps.renderStandings, deps.renderTournamentsPage, deps.showView: app-wide
//    renderers/navigation called after create/edit/template actions.

import { escapeHtml } from '../utils/formatting.js';
import { teamSettingsFrom, konradSettingsFrom, accelerationSettingsFrom, defaultSeedColor, getTeams } from '../state.js';
import { state, saveToLocalStorage, saveTemplates, saveTournamentOrg, addGroup, getActiveSettings, stampActiveTournamentClosed, applyGroupSettingsToAll, mergeGroups, renameGroupClass } from '../state.js';
import {
    GROUP_INPUT_FIELDS,
    buildDraftTournament,
    buildGeneralFormData,
    buildMergePicker,
    buildNewTournament,
    buildRatingListOptions,
    buildSingleGroupFormData,
    buildTemplateDraftTournament,
    buildTemplateSettings,
    coerceGroupInputValue,
    combineFormData,
    editFormValues,
    findGroupById,
    folderAssignmentChange,
    folderIdFor,
    formatPanelVisibility,
    formatTemplateSummary,
    groupFormValues,
    groupsOf,
    hasMultipleGroups,
    isDraftId,
    isRegistrationClosedByPairing,
    makeFolderRecord,
    mergeEditedTournament,
    modalGroupsVisible,
    nominatimAddressLabel,
    nominatimPrimaryLabel,
    parseRatingListValue,
    playersWithUpdatedRatings,
    promoteDraftTournament,
    registrationApiPatch,
    sortFoldersByName,
    suggestedRoundCount,
    templateCountHint,
    templateFormValues,
    templateHasGroups,
    templateSingleGroupValues,
    validateTournamentForm
} from '../utils/tournament-form-logic.js';
import { welcomePageIsEmpty, normalizeWelcomePage } from './welcome-editor.js';
import { openCategoryEditor, categorySummaryText } from './prizes.js';
import { openScheduleEditor, scheduleSummaryText } from './schedule.js';
import { mergeTombstones, makeTombstone } from '../utils/user-data-merge.js';
import { getSelectableDatabases } from './player-db-page.js';
import { listMyOrganizers } from '../services/organizers.js';
import { organizerOptions, defaultOrganizerId } from '../utils/organizer-logic.js';
import { renderRegistrationFieldsSummaries } from './registration-fields.js';
import { nsfCheckAvailable } from '../services/nsf-membership.js';
import { renderNsfResultsPanel } from './nsf-results-ui.js';

// The "NSF Membership Required" switches are there for every signed-in arbiter
// (js/services/nsf-membership.js). With one on, the series beside it says which kind of
// tournament this is — NGP, BGP or any other — and so who may enter
// (js/utils/nsf-membership.js nsfGroupRules); with it off, the series is hidden and means
// nothing. A hidden input keeps the value it was filled with.
function syncNsfSettingVisibility() {
    const show = nsfCheckAvailable();
    const sync = (settingId, switchId, seriesRowId) => {
        const setting = document.getElementById(settingId);
        const on = document.getElementById(switchId);
        const seriesRow = document.getElementById(seriesRowId);
        if (setting) setting.style.display = show ? '' : 'none';
        if (seriesRow) seriesRow.style.display = show && on && on.checked ? '' : 'none';
    };
    sync('tourney-nsf-setting', 'tourney-require-nsf', 'tourney-nsf-series-row');
    sync('group-nsf-setting', 'group-require-nsf-input', 'group-nsf-series-row');
}

// DOM Elements
const createTourneyModal = document.getElementById('create-tournament-modal');
const createTourneyForm = document.getElementById('create-tournament-form');
const btnCreateTourneyMain = document.getElementById('btn-create-tournament-main');
const btnCreateTourneyPage = document.getElementById('btn-create-tournament-page');
const btnEditTournament = document.getElementById('btn-edit-tournament');

const choiceModal = document.getElementById('new-tournament-choice-modal');
const chooseTemplateModal = document.getElementById('choose-template-modal');
const saveTemplateModal = document.getElementById('save-template-modal');

// The series follows its switch: shown while membership is required, hidden otherwise.
// The group's inputs are also GROUP_INPUT_FIELDS, whose own listeners write them onto the
// group; the results panel is redrawn after that, from what the group now says.
['tourney-require-nsf', 'group-require-nsf-input', 'tourney-nsf-series', 'group-nsf-series-input'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('change', () => {
        syncNsfSettingVisibility();
        setTimeout(renderNsfResultsPanel, 0);
    });
});

// Dependencies injected from app.js (see file header).
let deps = {};

// Explain a registration toggle that is off because the first round was paired,
// rather than because anyone switched it off (state.js
// closeRegistrationOnFirstPairing sets the flag). Silent in every other case.
function setRegClosedNote(noteId, src) {
    const note = document.getElementById(noteId);
    if (!note) return;
    note.style.display = isRegistrationClosedByPairing(src) ? 'block' : 'none';
}

export function renderModalGroupsTabs() {
    const groupsRow = document.getElementById('modal-groups-row');
    const container = document.getElementById('modal-groups-tabs');
    const btnGeneral = document.getElementById('btn-edit-general-info');
    const singleGroupContainer = document.getElementById('modal-single-group-settings-container');
    if (!groupsRow || !container || !btnGeneral) return;

    // Check if we are in Edit mode
    const isEdit = document.getElementById('create-tournament-modal')?.dataset.mode === 'edit';

    // In Create mode, only show groups if the active tournament is a draft.
    const hasGroups = modalGroupsVisible(state.activeTournament, isEdit);

    // Explicitly toggle visibility of legacy add group button
    const btnAddGroupLegacy = document.getElementById('btn-add-group-legacy');
    if (btnAddGroupLegacy) {
        btnAddGroupLegacy.style.display = hasGroups ? 'none' : 'inline-flex';
    }

    if (hasGroups) {
        groupsRow.style.display = 'flex';
        btnGeneral.style.display = 'inline-flex';
        if (singleGroupContainer) singleGroupContainer.style.display = 'none';

        container.innerHTML = state.activeTournament.groups.map(group => {
            const isActive = String(group.id) === String(state.modalActiveGroupId);
            const btnClass = isActive ? 'btn-primary' : 'btn-outline';
            return `<button type="button" class="${btnClass}" data-group-id="${escapeHtml(group.id)}" style="padding: 0.35rem 0.75rem; font-size: 0.75rem; border-radius: 6px;">${escapeHtml(group.name)}</button>`;
        }).join('');

        // Wire up tab buttons
        container.querySelectorAll('button').forEach(btn => {
            btn.addEventListener('click', () => {
                const groupId = btn.dataset.groupId;
                switchModalTab(groupId);
            });
        });
    } else {
        groupsRow.style.display = 'none';
        btnGeneral.style.display = 'none';
        if (singleGroupContainer) singleGroupContainer.style.display = 'flex';
        switchModalTab('general');
    }
}

export function switchModalTab(target) {
    const btnGeneral = document.getElementById('btn-edit-general-info');
    const generalSection = document.getElementById('modal-general-section');
    const groupSection = document.getElementById('modal-group-settings-section');

    if (!btnGeneral || !generalSection || !groupSection) return;

    if (target === 'general') {
        state.modalActiveGroupId = null;
        generalSection.style.display = 'block';
        groupSection.style.display = 'none';

        btnGeneral.className = 'btn-primary'; // Highlight general tab
        // Unhighlight group tabs
        document.querySelectorAll('#modal-groups-tabs button').forEach(btn => {
            btn.className = 'btn-outline';
        });
    } else {
        state.modalActiveGroupId = target;
        generalSection.style.display = 'none';
        groupSection.style.display = 'flex';

        btnGeneral.className = 'btn-outline'; // Unhighlight general tab
        // Highlight active group tab
        document.querySelectorAll('#modal-groups-tabs button').forEach(btn => {
            const isActive = String(btn.dataset.groupId) === String(target);
            btn.className = isActive ? 'btn-primary' : 'btn-outline';
        });

        // Load active group settings into inputs
        const group = findGroupById(groupsOf(state.activeTournament), target);
        if (group) {
            // Defaults for every blank field live in groupFormValues()
            // (js/utils/tournament-form-logic.js).
            const v = groupFormValues(group);
            document.getElementById('group-name-input').value = v.name;
            document.getElementById('group-format-input').value = v.format;
            document.getElementById('group-type-input').value = v.type;
            document.getElementById('group-rounds-input').value = v.roundsCount;
            document.getElementById('group-seed-color-input').value = v.seedColor;
            document.getElementById('group-points-input').value = v.pointSystem;

            // Group dates options
            const useDatesCheckbox = document.getElementById('group-use-dates-input');
            if (useDatesCheckbox) {
                useDatesCheckbox.checked = v.useGroupDates;
                const datesContainer = document.getElementById('group-dates-container');
                if (datesContainer) {
                    datesContainer.style.display = v.useGroupDates ? 'flex' : 'none';
                }
            }
            document.getElementById('group-start-date-input').value = v.startDate;
            document.getElementById('group-start-time-input').value = v.startTime;

            // Group online registration options
            const regOpenCheckbox = document.getElementById('group-reg-open-input');
            if (regOpenCheckbox) {
                regOpenCheckbox.checked = v.registrationOpen;
                const regOptions = document.getElementById('group-reg-options');
                if (regOptions) regOptions.style.display = v.registrationOpen ? 'flex' : 'none';
            }
            document.getElementById('group-reg-deadline-input').value = v.registrationDeadline;
            document.getElementById('group-reg-deadline-time-input').value = v.registrationDeadlineTime;
            document.getElementById('group-max-participants-input').value = v.maxParticipants;
            document.getElementById('group-waiting-list-input').checked = v.waitingList;
            document.getElementById('group-require-phone-input').checked = v.requirePhone;
            document.getElementById('group-require-nsf-input').checked = v.requireNsfMembership;
            document.getElementById('group-nsf-series-input').value = v.nsfSeries;
            document.getElementById('group-reg-approval-input').value = v.registrationApproval;
            document.getElementById('group-keep-reg-open-input').checked = v.keepRegistrationOpen;
            setRegClosedNote('group-reg-closed-note', group);

            // The two that are not plain fields: a nested object and a list.
            fillGroupAcceleration(v.acceleration);
            syncGroupAccelerationPanel();
            renderGroupCategoriesSummary();
            renderGroupScheduleSummary();
        }
    }
    // The NSF switch, and the series beside it, are per group.
    syncNsfSettingVisibility();
}

export function initModalGroupInputs() {
    // id -> group property, and the value coercion each property needs, both live
    // in js/utils/tournament-form-logic.js.
    const inputs = GROUP_INPUT_FIELDS;

    const toggleGroupDatesContainer = (checked) => {
        const datesContainer = document.getElementById('group-dates-container');
        if (datesContainer) {
            datesContainer.style.display = checked ? 'flex' : 'none';
        }
    };

    const toggleGroupRegOptions = (checked) => {
        const regOptions = document.getElementById('group-reg-options');
        if (regOptions) {
            regOptions.style.display = checked ? 'flex' : 'none';
        }
    };

    for (const [id, prop] of Object.entries(inputs)) {
        const el = document.getElementById(id);
        if (el) {
            const isCheckbox = el.type === 'checkbox';
            const getVal = () =>
                coerceGroupInputValue(prop, isCheckbox ? el.checked : el.value, isCheckbox);

            const handler = () => {
                if (!state.activeTournament || !state.modalActiveGroupId) return;
                const group = findGroupById(groupsOf(state.activeTournament), state.modalActiveGroupId);
                if (group) {
                    const val = getVal();

                    // Renaming a group carries its players' class along, since that
                    // class was the group's name in the first place. Players who
                    // carry some other class — typed by the arbiter, or inherited
                    // from a group they were merged out of — keep it.
                    if (prop === 'name' && val && val !== group.name) {
                        const isActive = String(group.id) === String(state.activeGroupId);
                        renameGroupClass(isActive ? state.players : group.players, group.name, val);
                    }

                    group[prop] = val;

                    if (prop === 'useGroupDates') {
                        toggleGroupDatesContainer(val);
                    }

                    if (prop === 'registrationOpen') {
                        toggleGroupRegOptions(val);
                    }

                    // If they edited the name, refresh the modal tabs immediately
                    if (prop === 'name') {
                        renderModalGroupsTabs();
                    }
                }
            };

            el.addEventListener('input', handler);
            el.addEventListener('change', handler);
        }
    }

    initGroupExtraInputs();

    // Wire up format change listener to sync seed color for Monrad / Konrad
    const groupFormatInput = document.getElementById('group-format-input');
    if (groupFormatInput) {
        groupFormatInput.addEventListener('change', () => {
            if (!state.activeTournament || !state.modalActiveGroupId) return;
            const group = state.activeTournament.groups.find(g => String(g.id) === String(state.modalActiveGroupId));
            if (group) {
                const seedColorInput = document.getElementById('group-seed-color-input');
                if (seedColorInput) {
                    seedColorInput.value = defaultSeedColor(groupFormatInput.value);
                    group.seedColor = seedColorInput.value;
                }
            }
        });
    }
}

// Fill the create/edit modal's Folder <select> from state.tournamentOrg.folders,
// preselecting `selectedFolderId` (or "No folder" when falsy).
function populateModalFolderSelect(selectedFolderId) {
    const sel = document.getElementById('tourney-folder');
    if (!sel) return;
    const folders = sortFoldersByName((state.tournamentOrg && state.tournamentOrg.folders) || []);
    sel.innerHTML = `<option value="">${tr('No folder')}</option>` +
        folders.map(f => `<option value="${escapeHtml(String(f.id))}">${escapeHtml(f.name)}</option>`).join('');
    sel.value = selectedFolderId ? String(selectedFolderId) : '';
}

// ── The Organizer field ─────────────────────────────────────────────────────
//
// Which club runs the tournament (js/ui/organizers-page.js). The dropdown holds
// the organizers this arbiter belongs to; the field is hidden altogether for an
// arbiter who belongs to none, unless the tournament already names one. The
// tournament stores the id and a copy of the name (tournamentOrganizerFields).

// Profiles of the organizers in the dropdown, for "Use the organizer's details".
let formOrganizers = [];
// Guards against an older, slower load overwriting a newer one.
let organizerLoadToken = 0;

function lastOrganizerKey() {
    const user = window.authApp && window.authApp.getCurrentUser ? window.authApp.getCurrentUser() : null;
    return user ? 'lastOrganizerId:' + user.uid : '';
}

function readLastOrganizerId() {
    try {
        const key = lastOrganizerKey();
        return key ? (localStorage.getItem(key) || '') : '';
    } catch (e) {
        return '';
    }
}

function rememberOrganizerChoice(organizerId) {
    try {
        const key = lastOrganizerKey();
        if (!key) return;
        if (organizerId) localStorage.setItem(key, organizerId);
        else localStorage.removeItem(key);
    } catch (e) { /* localStorage may be unavailable */ }
}

function renderOrganizerOptions(options, selectedId) {
    const sel = document.getElementById('tourney-organizer');
    const group = document.getElementById('tourney-organizer-group');
    if (!sel) return;
    sel.innerHTML = `<option value="">${escapeHtml(tr('No organizer'))}</option>` +
        options.map(o => `<option value="${escapeHtml(o.value)}" data-name="${escapeHtml(o.label)}">${escapeHtml(o.label)}</option>`).join('');
    sel.value = options.some(o => o.value === selectedId) ? selectedId : '';
    if (group) group.style.display = options.length > 0 ? '' : 'none';
    syncOrganizerContactButton();
}

// Fill the dropdown: at once with what the tournament already names, then with
// the arbiter's own organizers once they have loaded. A new tournament starts
// with the organizer used last time.
function populateOrganizerSelect(current, isCreate) {
    const token = ++organizerLoadToken;
    const cur = { organizerId: (current && current.organizerId) || '', organizerName: (current && current.organizerName) || '' };
    formOrganizers = [];
    renderOrganizerOptions(organizerOptions([], cur), cur.organizerId);

    const signedIn = window.authApp && window.authApp.isAuthenticated && window.authApp.isAuthenticated();
    if (!signedIn) return;
    listMyOrganizers().then(mine => {
        if (token !== organizerLoadToken) return;
        const sel = document.getElementById('tourney-organizer');
        // The arbiter may already have picked something while this loaded.
        const picked = sel && sel.value !== cur.organizerId ? sel.value : null;
        formOrganizers = mine;
        const options = organizerOptions(mine, cur);
        const selected = picked !== null ? picked
            : (isCreate ? defaultOrganizerId(options, readLastOrganizerId()) : cur.organizerId);
        renderOrganizerOptions(options, selected);
    }).catch(err => console.warn('Could not load organizers for the tournament form:', err));
}

function selectedOrganizerProfile() {
    const sel = document.getElementById('tourney-organizer');
    const id = sel ? sel.value : '';
    return id ? formOrganizers.find(o => o.id === id) || null : null;
}

function syncOrganizerContactButton() {
    const btn = document.getElementById('btn-use-organizer-contact');
    if (!btn) return;
    const org = selectedOrganizerProfile();
    btn.style.display = org && (org.email || org.phone) ? '' : 'none';
}

function useOrganizerContact() {
    const org = selectedOrganizerProfile();
    if (!org) return;
    if (org.email) document.getElementById('tourney-arbiter-email').value = org.email;
    if (org.phone) document.getElementById('tourney-arbiter-phone').value = org.phone;
}

// Persist the modal's chosen folder onto the (now final-id) tournament. No-op /
// no cloud write when unchanged (folderAssignmentChange returns null).
function applyModalFolderSelection(tournamentId) {
    const sel = document.getElementById('tourney-folder');
    if (!sel || !tournamentId) return;
    if (!state.tournamentOrg) state.tournamentOrg = { folders: [], items: {} };
    if (!state.tournamentOrg.items) state.tournamentOrg.items = {};
    const record = folderAssignmentChange(state.tournamentOrg, tournamentId, sel.value || null);
    if (!record) return;
    state.tournamentOrg.items[tournamentId] = record;
    saveTournamentOrg();
}

export function openEditTournamentModal() {
    if (!state.activeTournament) return;

    createTourneyModal.dataset.mode = 'edit';
    createTourneyModal.querySelector('h2').textContent = tr('Edit Tournament Info');
    const btnSave = document.getElementById('btn-save-tournament-main');
    if (btnSave) btnSave.textContent = tr('Save Changes');

    // Stored tournament -> form values (defaults for every blank field) is
    // editFormValues() in js/utils/tournament-form-logic.js.
    const editSettings = getActiveSettings();
    const v = editFormValues(state.activeTournament, editSettings);

    document.getElementById('tourney-name').value = v.name;
    document.getElementById('tourney-start').value = v.startDate;
    document.getElementById('tourney-start-time').value = v.startTime;
    document.getElementById('tourney-end').value = v.endDate;
    document.getElementById('tourney-end-time').value = v.endTime;
    document.getElementById('tourney-format').value = v.format;
    document.getElementById('tourney-type').value = v.type;
    populateRatingListSelect(editSettings);
    document.getElementById('tourney-rounds').value = v.rounds;
    document.getElementById('tourney-seed-color').value = v.seedColor;
    document.getElementById('tourney-points').value = v.pointSystem;
    fillTeamSettings(editSettings.teamSettings);
    fillKonradSettings(editSettings.konradSettings);
    fillAccelerationSettings(editSettings.acceleration);
    syncTeamPanelVisibility();
    document.getElementById('tourney-location').value = v.location;
    document.getElementById('tourney-timecontrol').value = v.timeControl;
    document.getElementById('tourney-arbiter-name').value = v.arbiterName;
    document.getElementById('tourney-arbiter-email').value = v.arbiterEmail;
    document.getElementById('tourney-arbiter-phone').value = v.arbiterPhone;
    populateModalFolderSelect(folderIdFor(state.tournamentOrg, state.activeTournament.id));
    populateOrganizerSelect({ organizerId: v.organizerId, organizerName: v.organizerName }, false);
    document.getElementById('tourney-public').checked = v.isPublic;
    const pairingPrint = document.getElementById('tourney-pairing-print');
    if (pairingPrint) pairingPrint.value = v.pairingPrintStyle;
    const pairingPublish = document.getElementById('tourney-pairing-publish');
    if (pairingPublish) pairingPublish.value = v.pairingPublish;
    document.getElementById('tourney-welcome').value = v.welcomeMessage;

    // Load this tournament's rich welcome page into the editor draft (async; the
    // "Edit Welcome Page" button reads state.draftWelcomePage). Reset first so a
    // page from a previously edited tournament can't leak into this one.
    state.draftWelcomePage = null;
    // Same reasoning: a staged create-mode registration config must never leak into
    // an edit session, where the panel writes straight to the tournament instead.
    state.draftRegistrationApi = null;
    const editTid = state.activeTournament.id;
    if (editTid && window.authApp && window.authApp.getWelcomePage) {
        window.authApp.getWelcomePage(editTid).then(page => {
            // Ignore if the user already moved on to a different tournament.
            if (state.activeTournament && state.activeTournament.id === editTid) {
                // Migrate legacy block pages to the canvas model on load, so saving
                // the tournament without opening the editor can't drop the page.
                state.draftWelcomePage = welcomePageIsEmpty(page) ? null : normalizeWelcomePage(page);
            }
        }).catch(() => {});
    }

    // Online registration (tournament-level / single group)
    document.getElementById('tourney-reg-open').checked = v.registrationOpen;
    document.getElementById('tourney-reg-deadline').value = v.registrationDeadline;
    document.getElementById('tourney-reg-deadline-time').value = v.registrationDeadlineTime;
    document.getElementById('tourney-max-participants').value = v.maxParticipants;
    document.getElementById('tourney-waiting-list').checked = v.waitingList;
    document.getElementById('tourney-require-phone').checked = v.requirePhone;
    document.getElementById('tourney-require-nsf').checked = v.requireNsfMembership;
    document.getElementById('tourney-nsf-series').value = v.nsfSeries;
    document.getElementById('tourney-reg-approval').value = v.registrationApproval;
    document.getElementById('tourney-keep-reg-open').checked = v.keepRegistrationOpen;
    setRegClosedNote('tourney-reg-closed-note', state.activeTournament);
    toggleRegOptionsPanel();

    // Initialize groups tab state inside modal
    state.modalActiveGroupId = state.activeGroupId || null;
    renderModalGroupsTabs();
    if (state.modalActiveGroupId) {
        switchModalTab(state.modalActiveGroupId);
    } else {
        switchModalTab('general');
    }

    // Temp invites are a create-mode concept only; clear any leftovers so they
    // don't surface while editing an existing tournament.
    state.tempInvitedEmails = [];

    // Show Cooperating Arbiters section in Edit mode
    const coopSection = document.getElementById('cooperating-arbiters-section');
    if (coopSection) {
        coopSection.style.display = 'block';
        deps.renderCooperatingArbitersList();
    }

    setAdvancedOptionsOpen(false);
    syncNsfSettingVisibility();
    renderNsfResultsPanel();

    createTourneyModal.classList.add('active');
}

// Modal Logic
const openCreateModal = () => {
    // Check authentication
    if (window.authApp && !window.authApp.isAuthenticated()) {
        window.authApp.showLogin();
        return;
    }

    createTourneyModal.dataset.mode = 'create';
    createTourneyModal.querySelector('h2').textContent = tr('Create New Tournament');
    const btnSave = document.getElementById('btn-save-tournament-main');
    if (btnSave) btnSave.textContent = tr('Create Tournament');
    createTourneyForm.reset();
    // reset() fires no change event, so a format panel left open by an earlier edit
    // would otherwise stay on screen under a format that has gone back to Swiss.
    syncTeamPanelVisibility();
    populateModalFolderSelect('');
    populateOrganizerSelect(null, true);
    // The arbiter's rating lists have to be loaded here too, not only when
    // editing: without this the dropdown holds nothing but "No rating list", and
    // a tournament could only be bound to a list after creating it and reopening
    // the settings.
    populateRatingListSelect(null);

    // Clear any tie-breaks staged by a previous template selection
    state.draftTiebreaks = null;
    // Start a fresh welcome page for a new tournament (the hidden #tourney-welcome
    // summary is reset by createTourneyForm.reset()).
    state.draftWelcomePage = null;
    // Likewise the registration configuration: a new tournament starts from the
    // defaults, not from whatever the previous create session staged and abandoned.
    state.draftRegistrationApi = null;

    // Reset modal group settings to start with general info only
    state.modalActiveGroupId = null;
    renderModalGroupsTabs();
    switchModalTab('general');
    // A fresh tournament takes no entries from the outside until it is told to
    // (the checkbox's HTML default, restored by the form reset above); close the
    // options panel to match, and clear the "closed by pairing" note left over
    // from a previously edited tournament.
    setRegClosedNote('tourney-reg-closed-note', null);
    toggleRegOptionsPanel();
    // Show Cooperating Arbiters section and initialize temp invites
    state.tempInvitedEmails = [];
    const coopSection = document.getElementById('cooperating-arbiters-section');
    if (coopSection) {
        coopSection.style.display = 'block';
    }
    deps.renderCooperatingArbitersList();

    setAdvancedOptionsOpen(false);
    syncNsfSettingVisibility();
    renderNsfResultsPanel();

    createTourneyModal.classList.add('active');
};

// Every General-tab value, by the name buildDraftTournament / buildTemplateSettings /
// buildGeneralFormData use. Reading them is this file's job; deciding what a blank
// one means is theirs.
function readGeneralForm() {
    const val = (id) => document.getElementById(id)?.value ?? '';
    const checked = (id) => !!document.getElementById(id)?.checked;
    const organizerSel = document.getElementById('tourney-organizer');
    const organizerOpt = organizerSel && organizerSel.selectedOptions ? organizerSel.selectedOptions[0] : null;
    return {
        organizerId: val('tourney-organizer'),
        organizerName: organizerOpt && organizerOpt.value ? (organizerOpt.dataset.name || organizerOpt.textContent || '') : '',
        name: val('tourney-name'),
        startDate: val('tourney-start'),
        startTime: val('tourney-start-time'),
        endDate: val('tourney-end'),
        endTime: val('tourney-end-time'),
        format: val('tourney-format'),
        type: val('tourney-type'),
        rounds: val('tourney-rounds'),
        seedColor: val('tourney-seed-color'),
        pointSystem: val('tourney-points'),
        location: val('tourney-location'),
        timeControl: val('tourney-timecontrol'),
        arbiterName: val('tourney-arbiter-name'),
        arbiterEmail: val('tourney-arbiter-email'),
        arbiterPhone: val('tourney-arbiter-phone'),
        isPublic: checked('tourney-public'),
        pairingPrintStyle: val('tourney-pairing-print'),
        pairingPublish: val('tourney-pairing-publish'),
        welcomeMessage: val('tourney-welcome'),
        registrationOpen: checked('tourney-reg-open'),
        registrationDeadline: val('tourney-reg-deadline'),
        registrationDeadlineTime: val('tourney-reg-deadline-time'),
        requirePhone: checked('tourney-require-phone'),
        requireNsfMembership: checked('tourney-require-nsf'),
        nsfSeries: val('tourney-nsf-series'),
        registrationApproval: val('tourney-reg-approval'),
        keepRegistrationOpen: checked('tourney-keep-reg-open'),
        maxParticipants: val('tourney-max-participants'),
        waitingList: checked('tourney-waiting-list')
    };
}

// Turn the modal's General tab into the draft tournament that the groups editor
// hangs its groups off — see buildDraftTournament() for why every field travels.
function draftFromGeneralForm() {
    return buildDraftTournament(readGeneralForm(), {
        teamSettings: readTeamSettings(),
        konradSettings: readKonradSettings(),
        acceleration: readAccelerationSettings(),
        ratingListSelection: readRatingListSelection(),
        draftTiebreaks: state.draftTiebreaks
    });
}

// Shared by both "Add Group" buttons (the one in the groups row and the legacy
// one in General settings) — they did the same thing in two copies.
function addGroupFromModal() {
    const isEdit = createTourneyModal?.dataset.mode === 'edit';
    const isDraft = !!state.activeTournament && isDraftId(state.activeTournament.id);

    if (!isEdit && !isDraft) {
        // The draft replaces the real active tournament — stamp it as closed
        // first (see stampActiveTournamentClosed in state.js).
        stampActiveTournamentClosed();
        state.activeTournament = draftFromGeneralForm();
        state.players = [];
        state.rounds = [];
    }

    const newGroup = addGroup();
    if (newGroup) {
        state.modalActiveGroupId = newGroup.id;
        renderModalGroupsTabs();
        switchModalTab(newGroup.id);
    }
}

// "Apply to all groups": copy the group being edited onto every other group, so
// an event whose groups differ only by name (a Grand Prix, an age-class open)
// is configured once instead of once per group.
async function applyCurrentGroupSettingsToAll() {
    const groups = groupsOf(state.activeTournament);
    const source = findGroupById(groups, state.modalActiveGroupId);
    if (!source) return;

    if (!hasMultipleGroups(groups)) {
        await window.Dialog.warn(tr('There is only one group, so there is nothing to copy these settings to.'), tr('Only One Group'));
        return;
    }

    const ok = await window.Dialog.confirm(
        tr('Give all {n} groups the same settings as {group}? Format, rating type, rounds, point system, colour, tie-breaks, rating list and registration settings are copied. Names, dates, players and results are left alone.',
            { n: groups.length, group: source.name || tr('this group') }),
        tr('Apply to All Groups'),
        tr('Yes, Apply to All'),
        true
    );
    if (!ok) return;

    const changed = applyGroupSettingsToAll(source.id);
    saveToLocalStorage();
    renderModalGroupsTabs();
    switchModalTab(source.id);
    await window.Dialog.success(
        tr('{n} other groups now use these settings.', { n: changed }),
        tr('Settings Applied')
    );
}

// ── Merging groups ──────────────────────────────────────────────────────────
// Two groups that turned out to be one (a section that never filled up, an age
// class with four entries) get folded together instead of being run separately.
// The move itself is mergeGroups() in state.js — this is the picker around it.

function openMergeGroupModal() {
    // The ACTIVE group's rounds live on state.rounds rather than on the group.
    const roundsOf = (g) => (String(g.id) === String(state.activeGroupId) ? state.rounds : g.rounds) || [];
    const picker = buildMergePicker(groupsOf(state.activeTournament), state.modalActiveGroupId, roundsOf);

    if (!picker.ok) {
        if (picker.reason === 'only_one') {
            window.Dialog.warn(tr('There is only one group, so there is nothing to merge it with.'), tr('Only One Group'));
        } else if (picker.reason === 'already_paired') {
            window.Dialog.warn(
                tr('{group} has already been paired, so its players cannot be moved. Rounds already played were paired against a different field — colours, byes and tie-breaks all refer to it. Merge before the first round is generated.', { group: picker.source.name }),
                tr('Already Paired')
            );
        }
        return;
    }

    const select = document.getElementById('merge-target-select');
    if (select) {
        select.innerHTML = picker.options.map(o =>
            `<option value="${escapeHtml(o.value)}"${o.disabled ? ' disabled' : ''}>${escapeHtml(o.label)}</option>`
        ).join('');
        select.value = picker.value;
    }

    document.getElementById('merge-group-modal')?.classList.add('active');
}

async function confirmMergeGroups() {
    const groups = groupsOf(state.activeTournament);
    const source = findGroupById(groups, state.modalActiveGroupId);
    const targetId = document.getElementById('merge-target-select')?.value;
    const target = findGroupById(groups, targetId);
    if (!source || !target) return;

    const modal = document.getElementById('merge-group-modal');
    if (modal) modal.classList.remove('active');

    const ok = await window.Dialog.confirm(
        tr('Move every player from {source} into {target} and delete {source}? This cannot be undone.',
            { source: source.name, target: target.name }),
        tr('Merge Groups'),
        tr('Yes, Merge'),
        true
    );
    if (!ok) return;

    const result = mergeGroups(source.id, target.id);
    if (!result.ok) {
        if (result.error === 'rounds_exist') {
            await window.Dialog.warn(
                tr('{groups} have already been paired, so they cannot be merged. Merge before the first round is generated.',
                    { groups: (result.pairedNames || []).join(', ') }),
                tr('Already Paired')
            );
        }
        return;
    }

    state.modalActiveGroupId = target.id;
    saveToLocalStorage();
    renderModalGroupsTabs();
    switchModalTab(target.id);
    deps.updateUIState();
    deps.renderPlayerList();
    deps.renderCurrentRound();
    deps.renderStandings();

    await window.Dialog.success(
        tr('{n} players moved into {target}. They keep their old group name as their class.',
            { n: result.moved, target: result.targetName }),
        tr('Groups Merged')
    );
}

// Show/hide the tournament-level registration options sub-panel based on the toggle
function toggleRegOptionsPanel() {
    const toggle = document.getElementById('tourney-reg-open');
    const panel = document.getElementById('tourney-reg-options');
    if (panel) panel.style.display = (toggle && toggle.checked) ? 'flex' : 'none';

    // Shown while CREATING too, not just when editing. The link and the keys need a
    // saved tournament to exist first, but the field configuration does not — and an
    // organizer who never sees this button while setting the tournament up is
    // unlikely to go hunting for it in Options afterwards. In create mode the panel
    // opens in draft mode: fields are staged and committed with the tournament.
    const apiRow = document.getElementById('registration-api-row');
    if (apiRow) apiRow.style.display = 'block';

    // "Asks for 6 details, 3 of them required" under the fields button — read from
    // whatever is stored (or staged) right now, so the panel opens telling the truth.
    renderRegistrationFieldsSummaries();
}

// ── Tournament templates ────────────────────────────────────────────────────
// Pressing "Create New Tournament" opens a choice: start from scratch (same as
// before) or pre-fill from a saved template. Templates capture reusable settings
// only (no players, dates, name or location) and live in localStorage per device.

function openNewTournamentChoice() {
    // Check authentication up-front (mirrors openCreateModal)
    if (window.authApp && !window.authApp.isAuthenticated()) {
        window.authApp.showLogin();
        return;
    }

    const btnTemplate = document.getElementById('btn-choice-from-template');
    const hint = document.getElementById('choice-template-hint');
    const hasTemplates = Array.isArray(state.templates) && state.templates.length > 0;
    if (btnTemplate) {
        btnTemplate.disabled = !hasTemplates;
        btnTemplate.style.opacity = hasTemplates ? '1' : '0.45';
        btnTemplate.style.cursor = hasTemplates ? 'pointer' : 'not-allowed';
    }
    if (hint) {
        hint.textContent = templateCountHint(hasTemplates ? state.templates.length : 0);
    }

    choiceModal.classList.add('active');
}

function renderTemplateList() {
    const container = document.getElementById('template-list');
    if (!container) return;
    const templates = Array.isArray(state.templates) ? state.templates : [];

    if (templates.length === 0) {
        container.innerHTML = `<p style="text-align: center; color: var(--text-muted); padding: 1.5rem; font-style: italic;">${tr('No templates saved yet.')}</p>`;
        return;
    }

    container.innerHTML = templates.map(tmpl => `
        <div class="glass" data-template-id="${escapeHtml(tmpl.id)}" style="display: flex; align-items: center; gap: 0.75rem; padding: 0.85rem 1rem; border: 1px solid var(--border-color); border-radius: 10px; background: var(--surface-tint);">
            <button type="button" class="template-use-btn" data-template-id="${escapeHtml(tmpl.id)}" style="flex: 1; display: flex; flex-direction: column; align-items: flex-start; gap: 0.2rem; background: none; border: none; cursor: pointer; text-align: left; color: var(--text-main); padding: 0;">
                <span style="font-weight: 600;">${escapeHtml(tmpl.name)}</span>
                <small style="color: var(--text-muted);">${escapeHtml(formatTemplateSummary(tmpl))}</small>
            </button>
            <button type="button" class="template-delete-btn btn-icon-small btn-icon-danger" data-template-id="${escapeHtml(tmpl.id)}" title="${tr('Delete template')}" style="flex-shrink: 0;">
                <i class="ri-delete-bin-line"></i>
            </button>
        </div>
    `).join('');

    container.querySelectorAll('.template-use-btn').forEach(btn => {
        btn.addEventListener('click', () => useTemplate(btn.dataset.templateId));
    });
    container.querySelectorAll('.template-delete-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            deleteTemplate(btn.dataset.templateId);
        });
    });
}

async function deleteTemplate(id) {
    const tmpl = state.templates.find(t => String(t.id) === String(id));
    if (!tmpl) return;
    const ok = await window.Dialog.confirm(
        tr('Delete the template "{name}"? This cannot be undone.', { name: tmpl.name }),
        tr('Delete Template'), tr('Yes, Delete'), true
    );
    if (!ok) return;
    state.templates = state.templates.filter(t => String(t.id) !== String(id));
    // Record the delete so another device that still holds this template can't
    // resurrect it through the union-merge on load (user-data-merge.js).
    state.deletedTemplates = mergeTombstones(state.deletedTemplates, [makeTombstone(id)]);
    saveTemplates();
    renderTemplateList();
}

// Selecting a template: open the create modal and pre-fill it from the template.
function useTemplate(id) {
    const tmpl = state.templates.find(t => String(t.id) === String(id));
    if (!tmpl) return;
    chooseTemplateModal.classList.remove('active');
    openCreateModal();
    applyTemplateToForm(tmpl);
}

// Collect the current modal settings into a template `settings` object. Mirrors the
// fields the create/submit flow reads. Players, dates, name and location are excluded.
function gatherTemplateSettings() {
    const at = state.activeTournament;
    const isEdit = document.getElementById('create-tournament-modal')?.dataset.mode === 'edit';
    const groupsActive = modalGroupsVisible(at, isEdit);

    return buildTemplateSettings(readGeneralForm(), {
        groups: groupsActive ? at.groups : null,
        teamSettings: readTeamSettings(),
        konradSettings: readKonradSettings(),
        acceleration: readAccelerationSettings(),
        ratingListSelection: readRatingListSelection(),
        tiebreaks: (at && at.tiebreaks) ? at.tiebreaks : null
    });
}

// Pre-fill the (already open and reset) create modal from a template.
function applyTemplateToForm(tmpl) {
    const s = (tmpl && tmpl.settings) || {};

    // General / tournament-level fields
    const g = templateFormValues(s);
    document.getElementById('tourney-timecontrol').value = g.timeControl;
    document.getElementById('tourney-arbiter-name').value = g.arbiterName;
    document.getElementById('tourney-arbiter-email').value = g.arbiterEmail;
    document.getElementById('tourney-arbiter-phone').value = g.arbiterPhone;
    document.getElementById('tourney-public').checked = g.isPublic;
    document.getElementById('tourney-welcome').value = g.welcomeMessage;
    document.getElementById('tourney-reg-open').checked = g.registrationOpen;
    document.getElementById('tourney-reg-deadline-time').value = g.registrationDeadlineTime;
    document.getElementById('tourney-max-participants').value = g.maxParticipants;
    document.getElementById('tourney-waiting-list').checked = g.waitingList;
    document.getElementById('tourney-require-phone').checked = g.requirePhone;
    document.getElementById('tourney-require-nsf').checked = g.requireNsfMembership;
    document.getElementById('tourney-nsf-series').value = g.nsfSeries;
    document.getElementById('tourney-reg-approval').value = g.registrationApproval;
    document.getElementById('tourney-keep-reg-open').checked = g.keepRegistrationOpen;
    toggleRegOptionsPanel();
    syncNsfSettingVisibility();

    if (templateHasGroups(s)) {
        // Build a draft multi-group tournament so the create/submit flow treats it as multi-group.
        // The draft replaces the real active tournament — stamp that one as closed
        // first (the create-submit helper call can't reach it once it's a draft).
        stampActiveTournamentClosed();
        const val = (id) => document.getElementById(id)?.value ?? '';
        state.activeTournament = buildTemplateDraftTournament(s, {
            name: val('tourney-name'),
            startDate: val('tourney-start'),
            startTime: val('tourney-start-time'),
            endDate: val('tourney-end'),
            endTime: val('tourney-end-time'),
            location: val('tourney-location')
        });
        state.players = [];
        state.rounds = [];
        state.draftTiebreaks = null;
        state.modalActiveGroupId = state.activeTournament.groups[0].id;
        renderModalGroupsTabs();
        switchModalTab(state.activeTournament.groups[0].id);
    } else {
        // Single-group settings into the form inputs.
        const one = templateSingleGroupValues(s);
        document.getElementById('tourney-format').value = one.format;
        document.getElementById('tourney-type').value = one.type;
        populateRatingListSelect(s);
        document.getElementById('tourney-rounds').value = one.rounds;
        document.getElementById('tourney-seed-color').value = one.seedColor;
        document.getElementById('tourney-points').value = one.pointSystem;
        fillTeamSettings(one.teamSettings);
        fillKonradSettings(one.konradSettings);
        fillAccelerationSettings(one.acceleration);
        syncTeamPanelVisibility();
        // Staged so the submit handler can apply the template's tie-breaks (no UI input for them here).
        state.draftTiebreaks = one.tiebreaks;
    }
}

// --- Team settings ----------------------------------------------------------
// The team panel is one block of inputs shared by create and edit, so reading and
// writing it lives in a pair of functions rather than being spelled out at each of
// the four call sites. Every value goes through teamSettingsFrom() so a blank or
// hand-edited field lands on the same defaults the rest of the app assumes.

function readTeamSettings() {
    const v = (id) => document.getElementById(id)?.value;
    const c = (id) => !!document.getElementById(id)?.checked;
    return teamSettingsFrom({
        boards: v('team-boards'),
        pairingSystem: v('team-pairing-system'),
        primaryScore: v('team-primary-score'),
        matchPointSystem: v('team-match-points'),
        colourPrefType: v('team-colour-pref'),
        initialColour: v('team-initial-colour'),
        reservesBottomOnly: c('team-reserves-bottom'),
        captainsSetLineups: c('team-captains-lineups')
    });
}

function fillTeamSettings(src) {
    const t = teamSettingsFrom(src);
    const set = (id, value) => { const el = document.getElementById(id); if (el) el.value = value; };
    const check = (id, value) => { const el = document.getElementById(id); if (el) el.checked = !!value; };
    set('team-boards', t.boards);
    set('team-pairing-system', t.pairingSystem);
    set('team-primary-score', t.primaryScore);
    set('team-match-points', t.matchPointSystem);
    set('team-colour-pref', t.colourPrefType);
    set('team-initial-colour', t.initialColour);
    check('team-reserves-bottom', t.reservesBottomOnly);
    check('team-captains-lineups', t.captainsSetLineups);
}

// --- Konrad settings --------------------------------------------------------
// Same arrangement as the team panel above, and through konradSettingsFrom() for the
// same reason. The two counts are blank for "no limit" / "every round", which is how the
// participant maximum shows it too.

function readKonradSettings() {
    const v = (id) => document.getElementById(id)?.value;
    return konradSettingsFrom({
        maxMeetings: v('konrad-max-meetings'),
        byeValue: v('konrad-bye-value'),
        maxScoringByes: v('konrad-max-byes'),
        countedRounds: v('konrad-counted-rounds')
    });
}

function fillKonradSettings(src) {
    const k = konradSettingsFrom(src);
    const set = (id, value) => { const el = document.getElementById(id); if (el) el.value = value; };
    set('konrad-max-meetings', k.maxMeetings);
    set('konrad-bye-value', k.byeValue);
    set('konrad-max-byes', k.maxScoringByes || '');
    set('konrad-counted-rounds', k.countedRounds || '');
}

// --- Acceleration settings --------------------------------------------------
// Same arrangement as the two panels above. The round count belongs to the simple
// method alone — Baku derives its own from the tournament's round count — so its
// field is hidden rather than left on screen doing nothing.

function readAccelerationSettings() {
    const v = (id) => document.getElementById(id)?.value;
    return accelerationSettingsFrom({
        method: v('accel-method'),
        rounds: v('accel-rounds')
    });
}

function fillAccelerationSettings(src) {
    const a = accelerationSettingsFrom(src);
    const set = (id, value) => { const el = document.getElementById(id); if (el) el.value = value; };
    set('accel-method', a.method);
    set('accel-rounds', a.rounds);
    syncAccelerationFields();
}

/**
 * The round-count field and the sentence under the panel, both derived from what is
 * currently selected. The sentence is there because "accelerated" on its own tells an
 * arbiter nothing about what their field will actually get — it names the players and
 * the rounds affected, recomputed as they change either.
 */
function syncAccelerationFields() {
    const a = readAccelerationSettings();

    const roundsGroup = document.getElementById('accel-rounds-group');
    if (roundsGroup) roundsGroup.style.display = a.method === 'classic' ? '' : 'none';

    const note = document.getElementById('accel-description');
    if (!note) return;
    // The field being described is the one the form is about to create or is editing,
    // which for an existing tournament is the roster on screen.
    const totalRounds = parseInt(document.getElementById('tourney-rounds')?.value, 10) || 0;
    const playerCount = state.players.length;
    const described = window.Acceleration.describe(a, totalRounds, playerCount);
    note.textContent = described ? tr(described.key, described.params) : '';
}

// --- Per-group acceleration and prize categories ----------------------------
// The group editor's copies of the two panels above. Deliberately NOT entries in
// GROUP_INPUT_FIELDS: that table maps one input to one flat group property, and
// acceleration is a nested object whose two boxes have to be read together so the
// normaliser writes a whole one. Categories are a list, not a field at all, so they get
// a button onto the editor the Standings page already has.

function readGroupAcceleration() {
    const v = (id) => document.getElementById(id)?.value;
    return accelerationSettingsFrom({
        method: v('group-accel-method-input'),
        rounds: v('group-accel-rounds-input')
    });
}

function fillGroupAcceleration(src) {
    const a = accelerationSettingsFrom(src);
    const set = (id, value) => { const el = document.getElementById(id); if (el) el.value = value; };
    set('group-accel-method-input', a.method);
    set('group-accel-rounds-input', a.rounds);
}

/**
 * The panel's visibility, its round-count box and its sentence, all from the GROUP being
 * edited — its own format and round count, and its own players, which in a grouped
 * tournament are not the ones on screen.
 */
function syncGroupAccelerationPanel() {
    const group = state.modalActiveGroupId
        ? findGroupById(groupsOf(state.activeTournament), state.modalActiveGroupId)
        : null;

    const panel = document.getElementById('group-acceleration-panel');
    const show = formatPanelVisibility(document.getElementById('group-format-input')?.value).acceleration;
    if (panel) panel.style.display = show ? 'flex' : 'none';
    if (!show) return;

    const a = readGroupAcceleration();
    const roundsGroup = document.getElementById('group-accel-rounds-group');
    if (roundsGroup) roundsGroup.style.display = a.method === 'classic' ? '' : 'none';

    const note = document.getElementById('group-accel-description');
    if (!note) return;
    const totalRounds = parseInt(document.getElementById('group-rounds-input')?.value, 10) || 0;
    // The group's own roster. It is the ACTIVE group's players that live in state.players;
    // every other group carries its own, and counting the wrong ones would describe the
    // wrong field.
    const players = group
        ? (String(group.id) === String(state.activeGroupId) ? state.players : (group.players || []))
        : [];
    const described = window.Acceleration.describe(a, totalRounds, players.length);
    note.textContent = described ? tr(described.key, described.params) : '';
}

/** The group whose tab is showing in the modal, or null. */
function modalGroup() {
    return state.modalActiveGroupId
        ? findGroupById(groupsOf(state.activeTournament), state.modalActiveGroupId)
        : null;
}

function renderGroupCategoriesSummary() {
    const summary = document.getElementById('group-categories-summary');
    if (!summary) return;
    const group = modalGroup();
    summary.textContent = group ? categorySummaryText(group) : '';
}

function renderGroupScheduleSummary() {
    const summary = document.getElementById('group-schedule-summary');
    if (!summary) return;
    const group = modalGroup();
    summary.textContent = group ? scheduleSummaryText(group) : '';
}

function initGroupExtraInputs() {
    ['group-accel-method-input', 'group-accel-rounds-input'].forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;
        const write = () => {
            if (!state.activeTournament || !state.modalActiveGroupId) return;
            const group = findGroupById(groupsOf(state.activeTournament), state.modalActiveGroupId);
            if (!group) return;
            group.acceleration = readGroupAcceleration();
            saveToLocalStorage();
            syncGroupAccelerationPanel();
        };
        el.addEventListener('change', write);
        el.addEventListener('input', write);
    });

    // The round count and the format both change what the sentence says, and the format
    // decides whether the panel is there at all. The round count also moves the schedule
    // summary's denominator: nine rounds with three dates is not the same news as three.
    ['group-rounds-input', 'group-format-input'].forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;
        el.addEventListener('change', syncGroupAccelerationPanel);
        if (id === 'group-rounds-input') el.addEventListener('change', renderGroupScheduleSummary);
    });

    const categories = document.getElementById('btn-group-categories');
    if (categories) {
        categories.addEventListener('click', () => {
            if (!state.modalActiveGroupId) return;
            openCategoryEditor({
                groupId: state.modalActiveGroupId,
                onSaved: renderGroupCategoriesSummary
            });
        });
    }

    const schedule = document.getElementById('btn-group-schedule');
    if (schedule) {
        schedule.addEventListener('click', () => {
            if (!state.modalActiveGroupId) return;
            openScheduleEditor({
                groupId: state.modalActiveGroupId,
                onSaved: renderGroupScheduleSummary
            });
        });
    }
}

// Show the team panel only for a team tournament, the Konrad panel only for Konrad, the
// Arena's description only for an Arena, and the acceleration panel only for the Swiss
// system.
function syncTeamPanelVisibility() {
    const show = formatPanelVisibility(document.getElementById('tourney-format')?.value);
    const panel = document.getElementById('team-settings-panel');
    if (panel) panel.style.display = show.team ? 'flex' : 'none';
    const konradPanel = document.getElementById('konrad-settings-panel');
    if (konradPanel) konradPanel.style.display = show.konrad ? 'flex' : 'none';
    const arenaPanel = document.getElementById('arena-settings-panel');
    if (arenaPanel) arenaPanel.style.display = show.arena ? 'flex' : 'none';
    const accelPanel = document.getElementById('acceleration-settings-panel');
    if (accelPanel) accelPanel.style.display = show.acceleration ? 'flex' : 'none';
    if (show.acceleration) syncAccelerationFields();
}

// --- Advanced options -------------------------------------------------------
// The settings an arbiter sets once a year, if ever — their own database, the
// point system, the time control, acceleration, cooperating arbiters, and how
// pairings are printed and published — are marked .advanced-setting in
// index.html and hidden until the footer button asks for them (css/main.css).
// Where they then appear depends on the screen:
//  - 1024px and wider: the modal widens and they stand in a column of their own
//    beside the form (#advanced-options-panel). placeAdvancedSettings() moves
//    each block into its slot there, and back when the screen narrows.
//  - Narrower (phones, tablets held upright, small windows): they stay in their
//    places in the form, in their own colour.
// They never leave the form, so readGeneralForm(), the template builders, the
// submit handler and form.reset() are untouched by any of this: only where they
// are drawn moves.

const ADVANCED_COLUMNS_QUERY = '(min-width: 1024px)';

// One entry per slot in #advanced-options-panel: the .advanced-setting block it
// holds on a wide screen, and a marker where that block lives in the form.
let advancedSlots = [];

// Closed whenever the modal opens, create or edit, and opened only by the button.
// It does NOT spring open for a tournament that happens to use one of the settings
// inside: the point of the section is a form with six fewer things in it, and a
// form that quietly grows them back is not that form.
function setAdvancedOptionsOpen(open) {
    if (!createTourneyModal) return;
    createTourneyModal.classList.toggle('show-advanced', !!open);
    const btn = document.getElementById('btn-advanced-options');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
}

function placeAdvancedSettings(columns) {
    createTourneyModal.classList.toggle('advanced-columns', columns);
    // A moved node loses the focus; hand it back to the field being typed in
    // when the window is resized across the line.
    const focused = document.activeElement;
    for (const { slot, block, home } of advancedSlots) {
        if (columns) slot.append(block);
        else home.after(block);
    }
    if (focused && focused !== document.activeElement && createTourneyModal.contains(focused)) {
        focused.focus();
    }
    syncAdvancedPanel();
}

// A block in the column shows when its place in the form does: a group's Point
// System only on that group's tab, the tournament's own only when it has no
// groups. A heading shows while anything under it does.
function syncAdvancedPanel() {
    if (!createTourneyModal.classList.contains('advanced-columns')) return;
    for (const { slot, home } of advancedSlots) {
        slot.hidden = home.parentElement.getClientRects().length === 0;
    }
    document.querySelectorAll('#advanced-options-panel .advanced-group').forEach(group => {
        group.hidden = !advancedSlots.some(({ slot, block }) =>
            group.contains(slot) && !slot.hidden && block.style.display !== 'none');
    });
}

function initAdvancedOptionsLayout() {
    const panel = document.getElementById('advanced-options-panel');
    if (!createTourneyModal || !panel) return;

    advancedSlots = [...panel.querySelectorAll('[data-advanced-slot]')].map(slot => {
        const block = document.getElementById(slot.dataset.advancedSlot)?.closest('.advanced-setting');
        if (!block) return null;
        const home = document.createComment(` advanced setting: ${slot.dataset.advancedSlot} `);
        block.before(home);
        return { slot, block, home };
    }).filter(Boolean);

    const wide = window.matchMedia(ADVANCED_COLUMNS_QUERY);
    wide.addEventListener('change', e => placeAdvancedSettings(e.matches));
    placeAdvancedSettings(wide.matches);

    // The tabs, the groups bar and the format pickers show and hide parts of the
    // form through their inline style. The column follows whatever they do, so
    // none of them has to know it exists.
    new MutationObserver(syncAdvancedPanel).observe(createTourneyModal, {
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
    });
}

// Round calculation helper for Round Robin
function updateRoundCountSuggestion() {
    const format = document.getElementById('tourney-format')?.value;
    const roundsInput = document.getElementById('tourney-rounds');
    if (!format || !roundsInput) return;

    // A team round robin is counted in TEAMS, not players — four teams of four play
    // three rounds, not fifteen. Both fields are read so the pure helper can pick.
    const teamPairingSystem = document.getElementById('team-pairing-system')?.value;
    const isTeamRR = format === 'team' && teamPairingSystem === 'round-robin';
    const rounds = suggestedRoundCount({
        format,
        teamPairingSystem,
        playerCount: state.players.length,
        teamCount: isTeamRR ? getTeams().length : 0
    });
    if (rounds !== null) roundsInput.value = rounds;
}

// --- Database and rating list binding ---------------------------------------
// One <select> binds both halves, so its option values carry "dbId|listId". An
// empty value means "use FIDE ratings", which is what every tournament created
// before player databases existed does. The option model — which databases and
// lists are offered, and the fallback row for one that has gone away — is
// buildRatingListOptions() in js/utils/tournament-form-logic.js; this end just
// renders it.
//
// The list name is carried on the option as a data attribute rather than parsed
// back out of its text: the label is now grouped under the database name, and a
// list whose own name contains the separator used to come back mangled.

async function populateRatingListSelect(selected) {
    const select = document.getElementById('tourney-rating-list');
    if (!select) return;

    const model = buildRatingListOptions(await getSelectableDatabases(), selected);
    const option = (o) =>
        `<option value="${escapeHtml(o.value)}" data-list-name="${escapeHtml(o.listName)}">${escapeHtml(o.label)}</option>`;

    select.innerHTML = option(model.none) +
        model.groups.map(g =>
            `<optgroup label="${escapeHtml(g.label)}">${g.options.map(option).join('')}</optgroup>`
        ).join('');

    if (model.unavailable) {
        select.insertAdjacentHTML('beforeend', option(model.unavailable));
    }
    select.value = model.chosen;
}

function readRatingListSelection() {
    const select = document.getElementById('tourney-rating-list');
    const raw = select ? select.value : '';
    const opt = select && select.options[select.selectedIndex];
    return parseRatingListValue(raw, opt && opt.dataset.listName);
}

function updatePlayerRatingsForNewType(newType) {
    // Re-calculated from the stored per-discipline ratings; see
    // playersWithUpdatedRatings() for what happens to a player who has none.
    const updated = playersWithUpdatedRatings(state.players, deps.getPlayerDisplayRating);
    if (!updated) return;

    state.players = updated;
    deps.renderPlayerList();
}

// Wires the create/edit tournament modal, its groups editor, template UI, and
// the "Edit Tournament" button. `deps` supplies functions owned by app.js (see
// file header for the full list and why each is injected rather than imported).
export function initTournamentForm(injectedDeps) {
    deps = injectedDeps;

    const toggle = document.getElementById('tourney-reg-open');
    if (toggle) toggle.addEventListener('change', toggleRegOptionsPanel);

    // Choice modal buttons
    const btnChoiceScratch = document.getElementById('btn-choice-from-scratch');
    const btnChoiceTemplate = document.getElementById('btn-choice-from-template');
    if (btnChoiceScratch) {
        btnChoiceScratch.addEventListener('click', () => {
            choiceModal.classList.remove('active');
            openCreateModal();
        });
    }
    if (btnChoiceTemplate) {
        btnChoiceTemplate.addEventListener('click', () => {
            if (btnChoiceTemplate.disabled) return;
            choiceModal.classList.remove('active');
            renderTemplateList();
            chooseTemplateModal.classList.add('active');
        });
    }

    // "Advanced Options" button inside the create/edit modal footer, and where
    // the settings it reveals are drawn
    initAdvancedOptionsLayout();
    const btnAdvancedOptions = document.getElementById('btn-advanced-options');
    if (btnAdvancedOptions) {
        btnAdvancedOptions.addEventListener('click', () => {
            const open = !createTourneyModal.classList.contains('show-advanced');
            setAdvancedOptionsOpen(open);
            // Opening it from the footer puts the first revealed field on screen;
            // without this the arbiter presses a button and nothing visibly happens,
            // because everything it revealed is further up a scrolled modal body.
            // (In the wide layout they open in their own column, already in view.)
            if (open && !createTourneyModal.classList.contains('advanced-columns')) {
                const firstShown = createTourneyModal.querySelector(
                    '#modal-general-section:not([style*="display: none"]) .advanced-setting,'
                    + ' #modal-group-settings-section:not([style*="display: none"]) .advanced-setting'
                );
                if (firstShown) firstShown.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    }

    // "Save as Template" button inside the create/edit modal footer
    const btnSaveAsTemplate = document.getElementById('btn-save-as-template');
    if (btnSaveAsTemplate) {
        btnSaveAsTemplate.addEventListener('click', () => {
            const input = document.getElementById('template-name-input');
            if (input) input.value = '';
            saveTemplateModal.classList.add('active');
            if (input) setTimeout(() => input.focus(), 50);
        });
    }

    const btnConfirmSaveTemplate = document.getElementById('btn-confirm-save-template');
    if (btnConfirmSaveTemplate) {
        btnConfirmSaveTemplate.addEventListener('click', async () => {
            const input = document.getElementById('template-name-input');
            const name = input ? input.value.trim() : '';
            if (!name) {
                await window.Dialog.warn(tr('Please enter a name for the template.'), tr('Name Required'));
                if (input) input.focus();
                return;
            }
            state.templates.push({
                id: 'tmpl_' + Date.now(),
                name: name,
                createdAt: Date.now(),
                settings: gatherTemplateSettings()
            });
            saveTemplates();
            saveTemplateModal.classList.remove('active');
            await window.Dialog.success(tr('Template "{name}" saved. It will appear under "Choose Template" next time you create a tournament.', { name }), tr('Template Saved'));
        });
    }

    if (btnCreateTourneyMain) btnCreateTourneyMain.addEventListener('click', openNewTournamentChoice);
    if (btnCreateTourneyPage) btnCreateTourneyPage.addEventListener('click', openNewTournamentChoice);
    if (btnEditTournament) btnEditTournament.addEventListener('click', openEditTournamentModal);

    const tourneyFormatSelect = document.getElementById('tourney-format');
    if (tourneyFormatSelect) {
        tourneyFormatSelect.addEventListener('change', updateRoundCountSuggestion);
        tourneyFormatSelect.addEventListener('change', () => {
            const seedColorInput = document.getElementById('tourney-seed-color');
            if (seedColorInput) {
                seedColorInput.value = defaultSeedColor(tourneyFormatSelect.value);
            }
        });
        tourneyFormatSelect.addEventListener('change', syncTeamPanelVisibility);
    }

    // The acceleration note names the players and rounds affected, so it has to be redrawn
    // whenever either could have changed — including from the round-count box, which is not
    // part of the panel.
    ['accel-method', 'accel-rounds', 'tourney-rounds'].forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;
        el.addEventListener('change', syncAccelerationFields);
        el.addEventListener('input', syncAccelerationFields);
    });

    // Switching a team event to a round robin fixes its round count, so suggest it.
    const teamPairingSelect = document.getElementById('team-pairing-system');
    if (teamPairingSelect) {
        teamPairingSelect.addEventListener('change', updateRoundCountSuggestion);
        teamPairingSelect.addEventListener('change', syncTeamPanelVisibility);
    }

    // Form Submissions
    if (createTourneyForm) {
        // Location Autocomplete (using Nominatim for free address suggestions)
        const locationInput = document.getElementById('tourney-location');
        const suggestionsEl = document.getElementById('location-suggestions');
        let locationTimer = null;

        if (locationInput && suggestionsEl) {
            locationInput.addEventListener('input', (e) => {
                const query = e.target.value.trim();
                clearTimeout(locationTimer);
                if (query.length < 3) {
                    suggestionsEl.classList.add('hidden');
                    return;
                }

                locationTimer = setTimeout(async () => {
                    try {
                        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`);
                        if (!response.ok) {
                            console.warn(`Location search failed: HTTP ${response.status} from Nominatim.`);
                            suggestionsEl.classList.add('hidden');
                            return;
                        }
                        const data = await response.json();

                        if (data && data.length > 0) {
                            // Both label builders (the row's heading, and the
                            // address a picked row writes into the box) are pure
                            // and live in js/utils/tournament-form-logic.js.
                            suggestionsEl.innerHTML = data.map(item => `
                                <div class="search-result-item" style="padding: 0.75rem 1rem; border-bottom: 1px solid var(--border-color); cursor: pointer;">
                                    <div style="font-weight: 500; font-size: 0.9rem; color: var(--text-main);">${escapeHtml(nominatimPrimaryLabel(item))}</div>
                                    <div style="font-size: 0.75rem; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(item.display_name)}</div>
                                </div>
                            `).join('');

                            suggestionsEl.querySelectorAll('.search-result-item').forEach((el, index) => {
                                el.addEventListener('mousedown', (e) => {
                                    locationInput.value = nominatimAddressLabel(data[index]);
                                    suggestionsEl.classList.add('hidden');
                                });
                            });

                            suggestionsEl.classList.remove('hidden');
                        } else {
                            suggestionsEl.classList.add('hidden');
                        }
                    } catch (error) {
                        // Blocked by CSP connect-src, offline, or a network/parse
                        // failure. Log the real error rather than assuming throttling.
                        console.warn('Location search error:', error);
                        suggestionsEl.classList.add('hidden');
                    }
                }, 500);
            });

            locationInput.addEventListener('blur', () => {
                // Delay hiding to allow mousedown on suggestions to fire
                setTimeout(() => suggestionsEl.classList.add('hidden'), 200);
            });
        }

        // Location test button
        const btnTestLocation = document.getElementById('btn-test-location');
        if (btnTestLocation) {
            btnTestLocation.addEventListener('click', () => {
                const loc = document.getElementById('tourney-location').value;
                if (loc) {
                    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`, '_blank');
                } else {
                    window.Dialog.alert(tr('Please enter a location first.'));
                }
            });
        }

        const organizerSel = document.getElementById('tourney-organizer');
        if (organizerSel) organizerSel.addEventListener('change', syncOrganizerContactButton);
        const btnUseOrganizerContact = document.getElementById('btn-use-organizer-contact');
        if (btnUseOrganizerContact) btnUseOrganizerContact.addEventListener('click', useOrganizerContact);

        // Create a folder inline from the create/edit modal, then select it.
        const btnModalNewFolder = document.getElementById('btn-modal-new-folder');
        if (btnModalNewFolder) {
            btnModalNewFolder.addEventListener('click', async () => {
                const name = await window.Dialog.text(tr('Name your new folder'), tr('New Folder'), '', tr('Create'), tr('e.g. Club Championships'));
                if (!name) return;
                if (!state.tournamentOrg) state.tournamentOrg = { folders: [], items: {} };
                if (!Array.isArray(state.tournamentOrg.folders)) state.tournamentOrg.folders = [];
                const folder = makeFolderRecord(name);
                state.tournamentOrg.folders.push(folder);
                saveTournamentOrg();
                populateModalFolderSelect(folder.id);
            });
        }

        createTourneyForm.addEventListener('submit', (e) => {
            e.preventDefault();
            const isEdit = createTourneyModal.dataset.mode === 'edit';
            // Whether the settings live on groups rather than on this form. While
            // CREATING, state.activeTournament is still the tournament that was open
            // before — unless groups were drafted — so asking it directly would throw
            // away the new tournament's format, rounds, points and the rest whenever
            // that previous one happened to have groups.
            const hasGroups = modalGroupsVisible(state.activeTournament, isEdit);

            const values = readGeneralForm();
            const generalData = buildGeneralFormData(values);
            const singleGroupData = buildSingleGroupFormData(values, {
                teamSettings: readTeamSettings(),
                konradSettings: readKonradSettings(),
                acceleration: readAccelerationSettings(),
                ratingListSelection: readRatingListSelection()
            });

            // Combine inputs depending on whether groups are configured or not
            const formData = combineFormData(generalData, singleGroupData, hasGroups);

            // Programmatic Validations (name, public-tournament prerequisites)
            const check = validateTournamentForm(formData, state.userProfile);
            if (!check.ok) {
                if (check.kind === 'warn') window.Dialog.warn(check.message, check.title);
                else window.Dialog.error(check.message, check.title);
                return;
            }

            if (isEdit) {
                const oldType = getActiveSettings().type;
                state.activeTournament = mergeEditedTournament(state.activeTournament, formData);

                // If the rating type changed, update all player ratings (only if not multi-group)
                if (oldType !== formData.type && formData.type) {
                    updatePlayerRatingsForNewType(formData.type);
                }
            } else {
                // Creating a new tournament implicitly closes the current one (a
                // draft active is a no-op inside the helper; the real predecessor
                // was already stamped when the draft replaced it).
                stampActiveTournamentClosed();

                const isDraft = !!state.activeTournament && isDraftId(state.activeTournament.id);
                if (isDraft && state.activeTournament.groups) {
                    // Converting drafted draft tournament to real tournament
                    const promoted = promoteDraftTournament(state.activeTournament, formData, Date.now().toString());
                    state.activeTournament = promoted.tournament;

                    // Initialize active group
                    if (promoted.activeGroupId) {
                        state.activeGroupId = promoted.activeGroupId;
                        state.players = promoted.players;
                        state.rounds = promoted.rounds;
                    }
                } else {
                    state.activeTournament = buildNewTournament(formData, {
                        id: Date.now().toString(),
                        draftTiebreaks: state.draftTiebreaks,
                        invitedEmails: state.tempInvitedEmails
                    });
                    state.rounds = [];
                    state.players = []; // Reset players for new tournament
                    // New tournament has no groups — drop any cursor left over
                    // from a previously open multi-group tournament.
                    state.activeGroupId = null;
                }
            }

            // Registration link / API configuration set up during creation, before the
            // tournament had an id to attach it to. Applies to both create branches
            // above (drafted groups and plain), and never overwrites an existing
            // config while editing — the panel writes that one directly.
            const apiPatch = isEdit ? null : registrationApiPatch(state.draftRegistrationApi);
            if (apiPatch) {
                Object.assign(state.activeTournament, apiPatch);
                state.draftRegistrationApi = null;
            }

            const tournamentId = state.activeTournament.id;
            const tournamentName = state.activeTournament.name;
            const tempInvites = [...(state.tempInvitedEmails || [])];

            // Assign the modal's chosen folder now that the id is final (works for
            // both create and edit).
            applyModalFolderSelection(tournamentId);
            if (!isEdit) rememberOrganizerChoice(values.organizerId);

            saveToLocalStorage();
            deps.updateUIState();

            // Persist the rich welcome page to its own welcomePages/{id} document.
            // Only write when the editor was engaged this session (draft !== null) so
            // tournaments that never use the feature don't get an empty page doc.
            // Writing an empty page (after the arbiter clears all blocks) is intentional —
            // the spectator render falls back to the default layout when blocks is empty.
            if (state.draftWelcomePage !== null && window.authApp && window.authApp.saveWelcomePage && window.authApp.isAuthenticated()) {
                window.authApp.saveWelcomePage(tournamentId, state.draftWelcomePage, {
                    ownerId: state.activeTournament.ownerId,
                    isPublic: !!state.activeTournament.isPublic
                }).catch(err => console.error('Failed to save welcome page:', err));
                state.activeWelcomePage = welcomePageIsEmpty(state.draftWelcomePage)
                    ? null : normalizeWelcomePage(state.draftWelcomePage);
            }

            // Send invites if we created a new tournament and have tempInvites.
            // The tournament doc must exist in Firestore before we update its invite list,
            // so wait for the cloud save to finish first (otherwise the update is rejected
            // because the document does not exist yet).
            if (!isEdit && tempInvites.length > 0 && window.authApp && window.authApp.isAuthenticated()) {
                (async () => {
                    try {
                        await window.authApp.saveData(state);
                        await window.authApp.sendInvitesOnCreation(tournamentId, tournamentName, tempInvites);
                    } catch (err) {
                        console.error("Failed to send invites on creation:", err);
                    }
                })();
            }

            // Invites have been handled; clear the staging list so they aren't re-sent
            // or shown in a later create/edit session.
            state.tempInvitedEmails = [];

            // Template tie-breaks (if any) have been applied to the new tournament.
            state.draftTiebreaks = null;

            // Close modal first
            createTourneyModal.classList.remove('active');

            // Wait for the close transition before resetting (skip if the modal was reopened)
            setTimeout(() => {
                if (createTourneyModal.classList.contains('active')) return;
                createTourneyForm.reset();
                // Reset title/button for next time
                createTourneyModal.dataset.mode = 'create';
                createTourneyModal.querySelector('h2').textContent = tr('Create New Tournament');
                const btnSave = document.getElementById('btn-save-tournament-main');
                if (btnSave) btnSave.textContent = tr('Create Tournament');
            }, 300);

            deps.renderPlayerList();
            deps.renderCurrentRound();
            deps.renderStandings();

            if (!isEdit) {
                deps.showView('dashboard');
            }
        });

        // Wire up save button in modal footer
        const btnSaveTournamentMain = document.getElementById('btn-save-tournament-main');
        if (btnSaveTournamentMain) {
            btnSaveTournamentMain.addEventListener('click', () => {
                createTourneyForm.requestSubmit();
            });
        }

        // Wire up General Info button inside Edit Modal
        const btnEditGeneralInfo = document.getElementById('btn-edit-general-info');
        if (btnEditGeneralInfo) {
            btnEditGeneralInfo.addEventListener('click', () => {
                switchModalTab('general');
            });
        }

        // Wire up Add Group button inside modal
        const btnModalAddGroup = document.getElementById('btn-modal-add-group');
        if (btnModalAddGroup) {
            btnModalAddGroup.addEventListener('click', addGroupFromModal);
        }

        // Wire up Add Group Legacy button inside General settings
        const btnAddGroupLegacy = document.getElementById('btn-add-group-legacy');
        if (btnAddGroupLegacy) {
            btnAddGroupLegacy.addEventListener('click', addGroupFromModal);
        }

        // "Apply to all groups" inside the per-group settings tab
        const btnApplyToAllGroups = document.getElementById('btn-apply-group-settings-all');
        if (btnApplyToAllGroups) {
            btnApplyToAllGroups.addEventListener('click', applyCurrentGroupSettingsToAll);
        }

        // "Merge into another group" inside the per-group settings tab
        const btnMergeGroup = document.getElementById('btn-merge-group-modal');
        if (btnMergeGroup) {
            btnMergeGroup.addEventListener('click', openMergeGroupModal);
        }
        const btnConfirmMerge = document.getElementById('btn-confirm-merge-groups');
        if (btnConfirmMerge) {
            btnConfirmMerge.addEventListener('click', confirmMergeGroups);
        }

        // Wire up Delete Group button inside modal settings
        const btnDeleteGroupModal = document.getElementById('btn-delete-group-modal');
        if (btnDeleteGroupModal) {
            btnDeleteGroupModal.addEventListener('click', async () => {
                if (!state.activeTournament || !state.modalActiveGroupId) return;
                const groupToDelete = state.activeTournament.groups.find(g => String(g.id) === String(state.modalActiveGroupId));
                const groupName = groupToDelete ? groupToDelete.name : tr('this group');

                const confirmDelete = await window.Dialog.confirm(
                    tr('Are you sure you want to delete {group}? All players and pairings in this group will be lost permanently.', { group: groupName }),
                    tr('Delete Group'),
                    tr('Yes, Delete'),
                    true
                );
                if (confirmDelete) {
                    const index = state.activeTournament.groups.findIndex(g => String(g.id) === String(state.modalActiveGroupId));
                    if (index !== -1) {
                        state.activeTournament.groups.splice(index, 1);
                        if (state.activeTournament.groups.length > 0) {
                            const nextGroup = state.activeTournament.groups[0];
                            state.modalActiveGroupId = nextGroup.id;
                            renderModalGroupsTabs();
                            switchModalTab(nextGroup.id);
                        } else {
                            delete state.activeTournament.groups;
                            state.activeGroupId = null;
                            state.modalActiveGroupId = null;
                            renderModalGroupsTabs();
                        }
                    }
                }
            });
        }

        // Initialize modal group inputs listeners
        initModalGroupInputs();
    }
}
